<template>
  <div>
    <v-card max-width="1200" data-v-step="11" class="mx-auto" outlined>
      <v-container>
        <v-row justify="space-between" align="center">
          <v-col cols="6" xl="auto" class="order-0">
            <v-row align="baseline" class="ml-1">
              <v-icon class="mr-1">mdi-account-group</v-icon>
              <span class="headline">{{ $t('externalAuth.pluralTitle') }}</span>
              <span class="subtitle-2 font-weight-light ml-1">
                ({{ externalsAuthsCount }})
              </span>
            </v-row>
          </v-col>
          <v-col
            class="d-flex justify-end align-center order-1 order-xl-2"
            cols="6"
            xl="auto"
          >
            <v-tooltip bottom v-if="isAdminUser">
              <template
                v-slot:activator="{ on: onTooltip, attrs: attrsTooltip }"
              >
                <v-btn
                  class="primary secondary--text ml-1 mt-1 mb-1"
                  fab
                  elevation="1"
                  small
                  v-bind="{ ...attrsTooltip }"
                  v-on="{ ...onTooltip }"
                  :disabled="loading"
                  @click.stop="openExternalAuthDialog({}, 'create')"
                  id="create-externalAuth"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <div>
                {{ $t('externalAuth.actions.title.create') }}
              </div>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-container>

      <v-list-item>
        <v-list-item-content>
          <v-row class="pa-3">
            <v-text-field
              class="mr-1"
              v-model="search"
              prepend-icon="mdi-magnify"
              clearable
              :label="$t('externalAuth.search')"
              single-line
              hide-details
              :disabled="loading"
            />
          </v-row>
        </v-list-item-content>
      </v-list-item>
      <v-divider></v-divider>
      <AppSkeleton :condition="loading" type="externalAuthList">
        <template v-slot:skeleton-content>
          <v-list
            v-if="externalsAuths.length"
            max-height="400"
            class="overflow-y-auto"
          >
            <v-list-item
              v-for="(externalAuth, i) in pageExternalsAuths"
              :key="i"
              @click.stop="openExternalAuthDialog(externalAuth, 'display')"
              :id="'at-' + externalAuth.name"
            >
              <v-row
                :class="isMobile ? 'mb-3 px-4' : ''"
                justify="center"
                align="center"
              >
                <v-col cols="2">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ externalAuth.name }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-col>
                <v-col cols="1">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ externalAuth.type }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-col>
                <v-col cols="7">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ externalAuth.authority }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-col>
                <v-col cols="2">
                  <v-row justify="end" no-gutters>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          class="ma-1"
                          elevation="1"
                          icon
                          v-bind="attrs"
                          v-on="on"
                          :id="'update-externalAuth-' + externalAuth.name"
                          @click.stop="
                            openExternalAuthDialog(externalAuth, 'update')
                          "
                        >
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <div>
                        {{ $t('externalAuth.actions.title.update') }}
                      </div>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          class="ma-1"
                          elevation="1"
                          icon
                          v-bind="attrs"
                          v-on="on"
                          :id="'delete-externalAuth-' + externalAuth.name"
                          @click.stop="
                            openExternalAuthDialog(externalAuth, 'delete')
                          "
                        >
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <div>
                        {{ $t('externalAuth.actions.title.delete') }}
                      </div>
                    </v-tooltip>
                  </v-row>
                </v-col>
              </v-row>
            </v-list-item>
          </v-list>
          <v-card-text style="text-align: center" v-else>
            <span>
              {{ $t('externalAuth.noExternalsAuths') }}
            </span>
          </v-card-text>
        </template>
      </AppSkeleton>
      <v-divider />
      <v-card-actions class="headline justify-end" style="max-height: 50px">
        <PaginateBar
          :initialPageSize="10"
          :pageSizes="[10, 25, 50]"
          :search="search"
          :items="filteredExternalsAuths"
          @setPage="(value) => (page = value)"
          @setPageSize="(value) => (pageSize = value)"
        />
      </v-card-actions>
    </v-card>

    <ExternalAuthDialog
      v-if="externalAuthDialog"
      :externalsAuths="externalsAuths"
      :externalAuth="selectedExternalAuth"
      :action="action"
      @closeDialog="() => (externalAuthDialog = false)"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import ExternalAuthDialog from './ExternalAuthDialog.vue'
import PaginateBar from '@/components/commons/PaginateBar'

export default {
  props: {
    externalsAuths: Array,
    loading: Boolean,
  },
  components: {
    ExternalAuthDialog,
    PaginateBar,
  },
  data() {
    return {
      page: 0,
      pageSize: 0,
      search: '',
      selectedExternalAuth: undefined,
      action: '',
      externalAuthDialog: false,
    }
  },
  methods: {
    ...mapMutations(['setSnackbar']),
    /**
     * Opens the dialog to create/update/display/delete a external authentication.
     * @param {Object} externalAuth External authentication to open (empty when creating)
     * @param {String} action Action to perform (create, update, display, delete).
     */
    openExternalAuthDialog(externalAuth, action) {
      this.selectedExternalAuth = externalAuth
      this.action = action

      this.externalAuthDialog = true
    },
  },
  computed: {
    ...mapGetters(['isMobile', 'isAdminUser']),
    externalsAuthsCount() {
      return this.externalsAuths?.length ?? 0
    },
    filteredExternalsAuths() {
      const searchUpperCase = this.search ? this.search.toUpperCase() : ''
      let filtered = this.externalsAuths?.filter((externalAuth) =>
        externalAuth.name?.toUpperCase().includes(searchUpperCase)
      )
      return filtered
    },
    pageExternalsAuths() {
      return this.filteredExternalsAuths.slice(
        this.page * this.pageSize,
        (this.page + 1) * this.pageSize
      )
    },
  },
}
</script>
<style></style>
